import { useEffect, useState, useCallback } from "react";
import { Button } from "../components/ui/button";
import { Select } from "../components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { BarChart3, Loader2, Download, Filter } from "lucide-react";
import { fetchStatistics, fetchStatisticsDistinct } from "../services/crmServices/leadService";
import { useSelector } from "react-redux";
import { selectLeadStatuses } from "../store/slices/leadStatusSlice";
import { toast } from "sonner";

function isStatisticsPayload(data) {
    return (
        data &&
        typeof data === "object" &&
        typeof data.total === "number" &&
        Array.isArray(data.statuses)
    );
}

export function Statistics() {
    const leadStatuses = useSelector(selectLeadStatuses);

    const [sources, setSources] = useState([]);
    const [affiliates, setAffiliates] = useState([]);
    const [countries, setCountries] = useState([]);
    const [desks, setDesks] = useState([]);
    const [agents, setAgents] = useState([]);
    const [floorManagers, setFloorManagers] = useState([]);

    const [selectedSource, setSelectedSource] = useState("");
    const [selectedAffiliate, setSelectedAffiliate] = useState("");
    const [selectedCountry, setSelectedCountry] = useState("");
    const [selectedFloorManager, setSelectedFloorManager] = useState("");
    const [selectedDesk, setSelectedDesk] = useState("");
    const [selectedAgent, setSelectedAgent] = useState("");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");

    const [loading, setLoading] = useState(false);
    const [loadingFilters, setLoadingFilters] = useState(true);
    const [result, setResult] = useState(null);

    const loadInitialFilters = useCallback(async () => {
        setLoadingFilters(true);
        try {
            const response = await fetchStatisticsDistinct({});
            const data = response?.data;
            if (data) {
                setSources((data.sources || []).map((s) => ({ label: s, value: s })));
                setAffiliates((data.affiliates || []).map((a) => ({ label: a, value: a })));
                setCountries((data.countries || []).map((c) => ({ label: c, value: c })));
                setDesks((data.desks || []).map((d) => ({ label: d.name, value: d._id })));
                setAgents(
                    (data.agents || []).map((a) => ({
                        label: `${a.firstName} ${a.lastName}`,
                        value: a._id
                    }))
                );
                setFloorManagers(
                    (data.floorManagers || []).map((fm) => ({
                        label: `${fm.firstName} ${fm.lastName} — ${fm.floor === "conversion" ? "Conversion" : "Retention"}`,
                        value: fm._id
                    }))
                );
            }
        } catch (error) {
            console.error("Error loading filters:", error);
            toast.error("Failed to load filter options");
        } finally {
            setLoadingFilters(false);
        }
    }, []);

    useEffect(() => {
        loadInitialFilters();
    }, [loadInitialFilters]);

    const trim = (v) => (typeof v === "string" ? v.trim() : "");

    const hasFilter = Boolean(
        trim(selectedSource) || trim(selectedAffiliate) || trim(selectedCountry) ||
        trim(selectedFloorManager) || trim(selectedDesk) || trim(selectedAgent) ||
        trim(dateFrom) || trim(dateTo)
    );

    const buildFilters = useCallback(() => {
        const filters = {};
        if (trim(selectedSource)) filters.source = trim(selectedSource);
        if (trim(selectedAffiliate)) filters.affiliate = trim(selectedAffiliate);
        if (trim(selectedCountry)) filters.country = trim(selectedCountry);
        if (trim(selectedFloorManager)) filters.floorManager = trim(selectedFloorManager);
        if (trim(selectedDesk)) filters.desk = trim(selectedDesk);
        if (trim(selectedAgent)) filters.agent = trim(selectedAgent);
        if (trim(dateFrom)) filters.dateFrom = trim(dateFrom);
        if (trim(dateTo)) filters.dateTo = trim(dateTo);
        return filters;
    }, [selectedSource, selectedAffiliate, selectedCountry, selectedFloorManager, selectedDesk, selectedAgent, dateFrom, dateTo]);

    const refreshDropdowns = useCallback(async (filters) => {
        try {
            const response = await fetchStatisticsDistinct(filters);
            const data = response?.data;
            if (!data) return;

            setSources((data.sources || []).map((s) => ({ label: s, value: s })));
            setAffiliates((data.affiliates || []).map((a) => ({ label: a, value: a })));
            setCountries((data.countries || []).map((c) => ({ label: c, value: c })));
            setDesks((data.desks || []).map((d) => ({ label: d.name, value: d._id })));
            setAgents(
                (data.agents || []).map((a) => ({
                    label: `${a.firstName} ${a.lastName}`,
                    value: a._id
                }))
            );
            setFloorManagers(
                (data.floorManagers || []).map((fm) => ({
                    label: `${fm.firstName} ${fm.lastName} — ${fm.floor === "conversion" ? "Conversion" : "Retention"}`,
                    value: fm._id
                }))
            );
        } catch (error) {
            console.error("Error refreshing filter options:", error);
        }
    }, []);

    const handleApplyFilters = useCallback(async () => {
        if (!hasFilter) {
            toast.error("Select at least one filter");
            return;
        }

        setLoading(true);
        try {
            const filters = buildFilters();
            const [statsResponse] = await Promise.all([
                fetchStatistics(filters),
                refreshDropdowns(filters)
            ]);
            const data = statsResponse?.data;
            if (isStatisticsPayload(data)) {
                setResult(data);
            } else {
                setResult(null);
                toast.error(statsResponse?.message || "Unexpected response from statistics API");
            }
        } catch (error) {
            console.error("Error fetching statistics:", error);
            setResult(null);
            toast.error(
                error.response?.data?.message ||
                error.message ||
                "Failed to load statistics."
            );
        } finally {
            setLoading(false);
        }
    }, [hasFilter, buildFilters, refreshDropdowns]);

    const handleClear = useCallback(async () => {
        setSelectedSource("");
        setSelectedAffiliate("");
        setSelectedCountry("");
        setSelectedFloorManager("");
        setSelectedDesk("");
        setSelectedAgent("");
        setDateFrom("");
        setDateTo("");
        setResult(null);
        loadInitialFilters();
    }, [loadInitialFilters]);

    const getStatusLabel = (statusValue) => {
        const config = leadStatuses.find((s) => s.value === statusValue);
        return config?.label || statusValue;
    };

    const getPercentage = (count, total) => {
        if (total === 0) return "0";
        return ((count / total) * 100).toFixed(1);
    };

    const getActiveFilterSummary = () => {
        const parts = [];
        if (trim(selectedSource)) parts.push(`Source: "${trim(selectedSource)}"`);
        if (trim(selectedAffiliate)) parts.push(`Affiliate: "${trim(selectedAffiliate)}"`);
        if (trim(selectedCountry)) parts.push(`Country: "${trim(selectedCountry)}"`);
        if (trim(selectedFloorManager)) {
            const fm = floorManagers.find((f) => f.value === selectedFloorManager);
            parts.push(`Floor: "${fm?.label || selectedFloorManager}"`);
        }
        if (trim(selectedDesk)) {
            const desk = desks.find((d) => d.value === selectedDesk);
            parts.push(`Desk: "${desk?.label || selectedDesk}"`);
        }
        if (trim(selectedAgent)) {
            const agent = agents.find((a) => a.value === selectedAgent);
            parts.push(`Agent: "${agent?.label || selectedAgent}"`);
        }
        if (trim(dateFrom)) parts.push(`From: ${dateFrom}`);
        if (trim(dateTo)) parts.push(`To: ${dateTo}`);
        return parts.join(" + ");
    };

    const downloadStatisticsCsv = () => {
        if (!result || !isStatisticsPayload(result)) {
            toast.error("Nothing to download");
            return;
        }

        const escapeCsv = (value) => {
            if (value === null || value === undefined) return "";
            const s = String(value);
            if (s.includes(",") || s.includes('"') || s.includes("\n") || s.includes("\r")) {
                return `"${s.replace(/"/g, '""')}"`;
            }
            return s;
        };

        const fmLabel = floorManagers.find((f) => f.value === selectedFloorManager)?.label || "";
        const deskLabel = desks.find((d) => d.value === selectedDesk)?.label || "";
        const agentLabel = agents.find((a) => a.value === selectedAgent)?.label || "";

        const lines = [];
        lines.push(["Filter Source", trim(selectedSource) || "(none)"].map(escapeCsv).join(","));
        lines.push(["Filter Affiliate", trim(selectedAffiliate) || "(none)"].map(escapeCsv).join(","));
        lines.push(["Filter Country", trim(selectedCountry) || "(none)"].map(escapeCsv).join(","));
        lines.push(["Filter Floor", fmLabel || "(none)"].map(escapeCsv).join(","));
        lines.push(["Filter Desk", deskLabel || "(none)"].map(escapeCsv).join(","));
        lines.push(["Filter Agent", agentLabel || "(none)"].map(escapeCsv).join(","));
        lines.push(["Filter Date From", trim(dateFrom) || "(none)"].map(escapeCsv).join(","));
        lines.push(["Filter Date To", trim(dateTo) || "(none)"].map(escapeCsv).join(","));
        lines.push(["Total Leads", String(result.total)].join(","));
        lines.push(["Generated At", new Date().toISOString()].map(escapeCsv).join(","));
        lines.push("");
        lines.push(["Status Label", "Status Value", "Count", "Percentage (%)"].map(escapeCsv).join(","));

        for (const row of result.statuses) {
            const label = getStatusLabel(row.status);
            const pct = getPercentage(row.count, result.total);
            lines.push(
                [label, row.status ?? "", String(row.count), pct].map(escapeCsv).join(",")
            );
        }

        if (result.countries && result.countries.length > 0) {
            lines.push("");
            lines.push(["Country", "Count", "Percentage (%)"].map(escapeCsv).join(","));
            for (const row of result.countries) {
                const pct = getPercentage(row.count, result.total);
                lines.push(
                    [row.country, String(row.count), pct].map(escapeCsv).join(",")
                );
            }
        }

        const csv = lines.join("\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        const safe = (s) =>
            String(s)
                .replace(/[^\w\-]+/g, "_")
                .replace(/_+/g, "_")
                .slice(0, 40) || "report";
        const parts = ["lead_statistics"];
        if (trim(selectedSource)) parts.push(safe(selectedSource));
        if (trim(selectedAffiliate)) parts.push(safe(selectedAffiliate));
        link.download = `${parts.join("_")}_${new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19)}.csv`;
        link.click();
        window.URL.revokeObjectURL(url);
        toast.success("CSV downloaded");
    };

    return (
        <div className="container mx-auto p-6 space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold tracking-tight flex items-center">
                        <BarChart3 className="h-6 w-6 mr-2" />
                        Statistics
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        Lead status breakdown — select filters and click Apply
                    </p>
                </div>
            </div>

            <Card>
                <CardHeader className="pb-3">
                    <CardTitle className="text-base">Filters</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="space-y-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                            <div>
                                <label className="text-sm font-medium mb-1.5 block">Source</label>
                                <Select
                                    options={sources}
                                    value={selectedSource}
                                    onChange={setSelectedSource}
                                    placeholder={loadingFilters ? "Loading..." : "Select source"}
                                    searchable
                                    disabled={loadingFilters}
                                />
                            </div>
                            <div>
                                <label className="text-sm font-medium mb-1.5 block">Affiliate</label>
                                <Select
                                    options={affiliates}
                                    value={selectedAffiliate}
                                    onChange={setSelectedAffiliate}
                                    placeholder={loadingFilters ? "Loading..." : "Select affiliate"}
                                    searchable
                                    disabled={loadingFilters}
                                />
                            </div>
                            <div>
                                <label className="text-sm font-medium mb-1.5 block">Country</label>
                                <Select
                                    options={countries}
                                    value={selectedCountry}
                                    onChange={setSelectedCountry}
                                    placeholder={loadingFilters ? "Loading..." : "Select country"}
                                    searchable
                                    disabled={loadingFilters}
                                />
                            </div>
                            <div>
                                <label className="text-sm font-medium mb-1.5 block">Floor</label>
                                <Select
                                    options={floorManagers}
                                    value={selectedFloorManager}
                                    onChange={setSelectedFloorManager}
                                    placeholder={loadingFilters ? "Loading..." : "Select floor manager"}
                                    searchable
                                    disabled={loadingFilters}
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                            <div>
                                <label className="text-sm font-medium mb-1.5 block">Desk</label>
                                <Select
                                    options={desks}
                                    value={selectedDesk}
                                    onChange={setSelectedDesk}
                                    placeholder={loadingFilters ? "Loading..." : "Select desk"}
                                    searchable
                                    disabled={loadingFilters}
                                />
                            </div>
                            <div>
                                <label className="text-sm font-medium mb-1.5 block">Agent</label>
                                <Select
                                    options={agents}
                                    value={selectedAgent}
                                    onChange={setSelectedAgent}
                                    placeholder={loadingFilters ? "Loading..." : "Select agent"}
                                    searchable
                                    disabled={loadingFilters}
                                />
                            </div>
                            <div>
                                <label className="text-sm font-medium mb-1.5 block">Date From</label>
                                <input
                                    type="date"
                                    value={dateFrom}
                                    onChange={(e) => setDateFrom(e.target.value)}
                                    className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                                />
                            </div>
                            <div>
                                <label className="text-sm font-medium mb-1.5 block">Date To</label>
                                <input
                                    type="date"
                                    value={dateTo}
                                    onChange={(e) => setDateTo(e.target.value)}
                                    className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                                />
                            </div>
                        </div>

                        <div className="flex items-center gap-2 pt-1">
                            <Button
                                type="button"
                                onClick={handleApplyFilters}
                                disabled={loading || !hasFilter}
                            >
                                {loading ? (
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                ) : (
                                    <Filter className="h-4 w-4 mr-2" />
                                )}
                                {loading ? "Loading..." : "Apply Filters"}
                            </Button>
                            <Button type="button" variant="outline" onClick={handleClear} disabled={loading}>
                                Clear All
                            </Button>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {result && (
                <Card>
                    <CardHeader className="pb-3">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                            <CardTitle className="text-base">
                                Status Breakdown — {getActiveFilterSummary()}
                            </CardTitle>
                            <div className="flex flex-wrap items-center gap-2">
                                <Badge variant="secondary" className="text-sm">
                                    {result.total.toLocaleString()} total leads
                                </Badge>
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={downloadStatisticsCsv}
                                >
                                    <Download className="h-4 w-4 mr-2" />
                                    Download CSV
                                </Button>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent>
                        {result.statuses.length === 0 ? (
                            <p className="text-sm text-muted-foreground text-center py-8">
                                No leads found matching the selected filters.
                            </p>
                        ) : (
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Status</TableHead>
                                        <TableHead className="text-right">Count</TableHead>
                                        <TableHead className="text-right">Percentage</TableHead>
                                        <TableHead className="w-[40%]">Distribution</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {result.statuses.map((row) => (
                                        <TableRow key={String(row.status)}>
                                            <TableCell className="font-medium">
                                                {getStatusLabel(row.status)}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                {row.count.toLocaleString()}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                {getPercentage(row.count, result.total)}%
                                            </TableCell>
                                            <TableCell>
                                                <div className="w-full bg-gray-100 rounded-full h-2.5">
                                                    <div
                                                        className="bg-indigo-600 h-2.5 rounded-full transition-all duration-500"
                                                        style={{
                                                            width: `${getPercentage(row.count, result.total)}%`
                                                        }}
                                                    />
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                    </CardContent>
                </Card>
            )}

            {result && result.countries && result.countries.length > 0 && (
                <Card>
                    <CardHeader className="pb-3">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                            <CardTitle className="text-base">
                                Country Breakdown
                            </CardTitle>
                            <Badge variant="secondary" className="text-sm w-fit">
                                {result.countries.length} {result.countries.length === 1 ? "country" : "countries"}
                            </Badge>
                        </div>
                    </CardHeader>
                    <CardContent>
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Country</TableHead>
                                    <TableHead className="text-right">Count</TableHead>
                                    <TableHead className="text-right">Percentage</TableHead>
                                    <TableHead className="w-[40%]">Distribution</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {result.countries.map((row) => (
                                    <TableRow key={String(row.country)}>
                                        <TableCell className="font-medium">
                                            {row.country}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            {row.count.toLocaleString()}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            {getPercentage(row.count, result.total)}%
                                        </TableCell>
                                        <TableCell>
                                            <div className="w-full bg-gray-100 rounded-full h-2.5">
                                                <div
                                                    className="bg-emerald-600 h-2.5 rounded-full transition-all duration-500"
                                                    style={{
                                                        width: `${getPercentage(row.count, result.total)}%`
                                                    }}
                                                />
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>
            )}

            {!result && !loading && !hasFilter && (
                <Card>
                    <CardContent className="py-16">
                        <div className="text-center text-muted-foreground">
                            <BarChart3 className="h-12 w-12 mx-auto mb-4 opacity-30" />
                            <p className="text-lg font-medium">Select at least one filter</p>
                            <p className="text-sm mt-1">
                                Choose your filters above and click Apply Filters to see the breakdown
                            </p>
                        </div>
                    </CardContent>
                </Card>
            )}

            {!result && !loading && hasFilter && (
                <Card>
                    <CardContent className="py-16">
                        <div className="text-center text-muted-foreground">
                            <Filter className="h-12 w-12 mx-auto mb-4 opacity-30" />
                            <p className="text-lg font-medium">Filters selected</p>
                            <p className="text-sm mt-1">
                                Click Apply Filters to see the status breakdown
                            </p>
                        </div>
                    </CardContent>
                </Card>
            )}
        </div>
    );
}
